import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, OnInit, Renderer2 } from '@angular/core';
import { DwButtonConfig, DwModalConfig, DwModalHandler, DwModalResult } from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';

export type OnboardingRole = 'student' | 'instructor' | 'admin';

interface OnboardingStep {
  eyebrow?: string;
  title: string;
  description: string;
  /** 16:9 brand media above the copy — the school's login side image, with a bottom scrim. */
  brandImage?: boolean;
  /** Renders a static in-app UI mock instead of an image (see template ngSwitch). */
  mock?: string;
  ctaLabel?: string;
}

interface OnboardingDot {
  index: number;
  isActive: boolean;
}

const STUDENT_STEPS: OnboardingStep[] = [
  {
    brandImage: true,
    title: 'Welcome to {schoolName}',
    description: 'This is your training home. Your courses, assignments, and the daily quiz all live on your dashboard. Here\'s a quick tour of what matters most.'
  },
  {
    mock: 'trainingCode', eyebrow: 'Step 2 of 7', title: 'Share your training code',
    description: 'Your profile has a training code that belongs to you alone. Give it to your instructor and they enter it once to add you to their student list. No email invite or approval step needed.'
  },
  {
    mock: 'market', eyebrow: 'Step 3 of 7', title: 'Pick or be assigned a course',
    description: 'Depending on how your school is set up, you either enroll yourself from the marketplace or your school assigns courses to you. Either way, they land in My Courses.'
  },
  {
    mock: 'home', eyebrow: 'Step 4 of 7', title: 'Continue learning',
    description: 'The Continue Learning card on your home page always points at your next lesson, so you can get back into training in one click.'
  },
  {
    mock: 'course', eyebrow: 'Step 5 of 7', title: 'Courses, modules, lessons',
    description: 'Every course is built from modules, and every module from lessons, most with a short quiz at the end. Assignments from your instructor show up with their due dates.'
  },
  {
    mock: 'streak', eyebrow: 'Step 6 of 7', title: 'Build a streak',
    description: 'Your study streak grows from taking the daily quiz or completing a lesson, module quiz, or ProTools. A weather day gives you one free miss each week. Use it to keep the streak going.'
  },
  {
    mock: 'quiz', eyebrow: 'Step 7 of 7', title: 'Take the daily quiz',
    description: 'One quick question set every day, right on your dashboard. It keeps checkride knowledge fresh in a few minutes, and streaks and scores track over time.',
    ctaLabel: 'Let\'s go'
  }
];

const INSTRUCTOR_STEPS: OnboardingStep[] = [
  {
    brandImage: true,
    title: 'Welcome to {schoolName}',
    description: 'This is where you track every student you train. Assignments, progress, and your roster live in one place. A quick tour of the essentials.'
  },
  {
    mock: 'code', eyebrow: 'Step 2 of 5', title: 'Add a student with their code',
    description: 'Every student has a training code on their profile. Ask them for it, enter it here, and they\'re added to your roster.'
  },
  {
    mock: 'students', eyebrow: 'Step 3 of 5', title: 'Your students at a glance',
    description: 'The My Students page shows everyone assigned to you, sorted by who needs attention. Stalled progress and overdue assignments surface first.'
  },
  {
    mock: 'assign', eyebrow: 'Step 4 of 5', title: 'Create and track assignments',
    description: 'Build an assignment once and send it to a student or a whole cohort. Due dates, completion, and scores roll up on the Assignments page.'
  },
  {
    mock: 'progress', eyebrow: 'Step 5 of 5', title: 'See where each student stands',
    description: 'Open a student to view their course progress by module and lesson, with last activity and time spent. Click play on any lesson to watch the video right from this page.',
    ctaLabel: 'Let\'s go'
  }
];

const ADMIN_STEPS: OnboardingStep[] = [
  {
    brandImage: true,
    title: 'Welcome to {schoolName}',
    description: 'You run the school side: users, courses, and reporting for everyone at {schoolName}. A quick tour of the controls.'
  },
  {
    mock: 'users', eyebrow: 'Step 2 of 7', title: 'Manage your users',
    description: 'Manage Users is the roster for your whole instance: every student, instructor, and admin in one list. Search, filter by role, and open anyone to see their courses and progress.'
  },
  {
    mock: 'invite', eyebrow: 'Step 3 of 7', title: 'Add users by email invite',
    description: 'Send a join request to add anyone to your instance: pick the request type, choose courses for students, then enter one or more email addresses. They set their own password and appear in Manage Users once they accept.'
  },
  {
    mock: 'personal', eyebrow: 'Step 4 of 7', title: 'Assign instructors to students',
    description: 'Open a student from Manage Users and use the Instructors field on their Personal Details to set who trains them. You can assign more than one instructor to a student.'
  },
  {
    mock: 'roles', eyebrow: 'Step 5 of 7', title: 'Instructor vs Lead Instructor',
    description: 'New instructors default to Instructor and see only their assigned students. You can change anyone to Lead Instructor, which shows every student at the school and allows reassigning students between instructors.'
  },
  {
    mock: 'adminReport', eyebrow: 'Step 6 of 7', title: 'Reporting across the school',
    description: 'See enrollment, progress, and quiz performance across every student and instructor. These are the numbers you need to keep training on track.'
  },
  {
    mock: 'settings', eyebrow: 'Step 7 of 7', title: 'Settings and white labeling',
    description: 'Settings is where your instance becomes your school: upload your logo, set the brand color that themes the navbar and buttons, and adjust the defaults everyone inherits.',
    ctaLabel: 'Let\'s go'
  }
];

const STEPS_BY_ROLE: { [role in OnboardingRole]: OnboardingStep[] } = {
  student: STUDENT_STEPS,
  instructor: INSTRUCTOR_STEPS,
  admin: ADMIN_STEPS
};

@Component({
  selector: 'app-student-onboarding-modal',
  templateUrl: './student-onboarding-modal.component.html'
})
export class StudentOnboardingModalComponent implements OnInit, AfterViewInit, DwModalHandler {
  modalConfig: DwModalConfig<{ role?: OnboardingRole; schoolName?: string }>;
  // Initialised eagerly (not just declared) so the modal host subscribes to
  // it during configureComponent() — our custom buttons drive the framework
  // close, since the framework header/footer are hidden for this modal.
  closeModal: EventEmitter<DwModalResult<any>> = new EventEmitter<DwModalResult<any>>();

  steps: OnboardingStep[] = STUDENT_STEPS;

  // View-model state precomputed per step change — templates never call methods.
  stepIndex = 0;
  currentStep: OnboardingStep = this.steps[0];
  dots: OnboardingDot[] = [];
  backVisibility = 'hidden';
  nextLabel = 'Next';
  schoolName = 'your school';
  brandImageStyle: { [property: string]: string } = {};

  // ── Static data behind the in-app UI mocks (mirrors the approved mockups) ──
  readonly marketCourses = [
    { img: 'assets/onboarding/private-pilot.webp', name: 'Private Pilot', status: 'Completed', modules: 'Module 23 / 23', pct: '100%', ringPct: 100, cta: 'Review', play: false },
    { img: 'assets/onboarding/instrument-rating.webp', name: 'Instrument Rating', status: 'In progress', modules: 'Module 5 / 20', pct: '26%', ringPct: 26, cta: 'Resume', play: true },
    { img: 'assets/onboarding/commercial-pilot.webp', name: 'Commercial Pilot', status: 'Ready to begin', modules: 'Module 0 / 18', pct: '0%', ringPct: 0, cta: 'Start', play: false }
  ].map(c => ({
    ...c,
    ringStyle: c.ringPct === 0
      ? 'conic-gradient(var(--gray-200) 0 100%)'
      : `conic-gradient(var(--dw-navbar-bg) ${c.ringPct}%, var(--gray-200) 0)`
  }));

  readonly marketplaceItems = [
    { img: 'assets/onboarding/multi-engine.webp', name: 'Multi-Engine', meta: '9 modules', price: 'Included with your school' },
    { img: 'assets/onboarding/cfi.webp', name: 'Flight Instructor', meta: '24 modules', price: 'Included with your school' }
  ];

  readonly courseStats = [
    { label: 'Modules', value: '6', total: '/23' },
    { label: 'Lessons', value: '99', total: '/394' },
    { label: 'Runtime', value: '37h 25m', total: '' }
  ];

  readonly courseTabs = [
    { label: 'In progress', count: '1' },
    { label: 'Completed', count: '6' },
    { label: 'Not started', count: '16' },
    { label: 'Endorsements', count: '0' }
  ];

  // Thursday (index 4) stays empty and becomes the animated weather day;
  // the rest check in staggered via per-slot keyframes (sob-day-*-N in SCSS).
  readonly streakDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, k) => {
    const slot = k === 4 ? -1 : (k < 4 ? k : k - 1);
    return {
      label,
      isWx: k === 4,
      ringClass: slot >= 0 ? 'sob-day-ring-' + slot : 'sob-day-ring-wx',
      checkClass: slot >= 0 ? 'sob-day-check-' + slot : ''
    };
  });

  readonly rosterStudents = [
    { initials: 'AC', delay: '1.65s', name: 'Alex Chen', login: '08/16/2026', courses: 'Private Pilot Airplane', pct: '73%' },
    { initials: 'MR', delay: '3.19s', name: 'Maya Ruiz', login: '08/15/2026', courses: 'Private Pilot Airplane, Instrument Rating', pct: '48%' },
    { initials: 'DO', delay: '4.73s', name: 'Devin Okafor', login: '08/14/2026', courses: 'Commercial Pilot Airplane', pct: '91%' },
    { initials: 'SP', delay: '6.27s', name: 'Sam Patel', login: '08/09/2026', courses: 'Instrument Rating', pct: '34%' },
    { initials: 'JL', delay: '7.81s', name: 'Jamie Lindqvist', login: '07/28/2026', courses: 'Commercial Multi-Engine Course', pct: '12%' },
    { initials: 'TB', delay: '9.35s', name: 'Tessa Brandt', login: '07/21/2026', courses: 'Private Pilot Airplane', pct: '60%' }
  ];

  readonly assignments = [
    { students: 'Alex Chen', course: 'Private Pilot Airplane', instructor: 'J. Mendez', assigned: '08/10/2026', due: '08/17/2026', status: 'Completed', kind: 'success' },
    { students: 'Maya Ruiz, Sam Patel', course: 'Instrument Rating', instructor: 'J. Mendez', assigned: '08/11/2026', due: '08/18/2026', status: 'Active', kind: 'info' },
    { students: 'Devin Okafor', course: 'Commercial Pilot Airplane', instructor: 'J. Mendez', assigned: '08/04/2026', due: '08/12/2026', status: 'Past due', kind: 'warning' },
    { students: 'Jamie Lindqvist, Tessa Brandt', course: 'Commercial Multi-Engine', instructor: 'J. Mendez', assigned: '08/12/2026', due: '08/20/2026', status: 'Active', kind: 'info' },
    { students: 'Private Ground, Fall 2026', course: 'Private Pilot Airplane', instructor: 'D. Whitfield', assigned: '07/28/2026', due: '08/14/2026', status: 'Completed', kind: 'success' },
    { students: 'Sam Patel', course: 'Instrument Rating', instructor: 'J. Mendez', assigned: '07/21/2026', due: '07/30/2026', status: 'Cancelled', kind: 'muted' }
  ];

  readonly progressLessons = [
    { name: '4.1 Weight & balance', status: 'Completed', statusClass: 'success', when: 'Aug 12, 2026', runtime: '14m' },
    { name: '4.2 Stalls & spin awareness', status: 'In progress', statusClass: 'warning', when: 'Last activity Aug 16', runtime: '6m' },
    { name: '4.3 Density altitude', status: 'Not started', statusClass: 'muted', when: 'No activity yet', runtime: '0m' }
  ];

  readonly roleTabs = [
    { icon: '◱', n: '9', label: 'Instructors' },
    { icon: '◲', n: '3', label: 'School admins' },
    { icon: '▦', n: '23', label: 'All' }
  ];

  readonly manageUsers = [
    { initials: 'BB', name: 'Ben Barlow', login: '06/20/2026', instructors: 'C. Aldridge, M. Reyes', courses: 'Private Pilot Airplane, Commercial Multi-Engine Course', groups: '—' },
    { initials: 'PC', name: 'Priya Chandra', login: '06/12/2026', instructors: 'C. Aldridge, M. Reyes', courses: '—', groups: 'Fall Semester 2026 · Full Track' },
    { initials: 'OD', name: 'Omar Deveraux', login: '05/27/2026', instructors: '—', courses: 'Commercial Pilot Airplane', groups: '—' },
    { initials: 'HK', name: 'Hana Kowalski', login: '05/26/2026', instructors: '—', courses: '—', groups: '—' },
    { initials: 'MO', name: 'Marcus Ostrander', login: '05/09/2026', instructors: '—', courses: 'Commercial Multi-Engine Course, Private Pilot Airplane', groups: '—' },
    { initials: 'LL', name: 'Linda Lundquist', login: '04/10/2026', instructors: 'Casey Aldridge', courses: 'Commercial Multi-Engine Course, Private Pilot Airplane', groups: '—' }
  ];

  readonly inviteCourses = ['Commercial Pilot Airplane', 'Private Pilot Airplane', 'Instrument Rating'];

  readonly instructorRows = [
    { initials: 'BB', name: 'Ben Barlow', instructor: 'You', course: 'Private Pilot Airplane', pct: '72%', last: 'Today' },
    { initials: 'MO', name: 'Marcus Ostrander', instructor: 'You', course: 'Commercial Multi-Engine', pct: '44%', last: '5 days' },
    { initials: 'TW', name: 'Tomas Weller', instructor: 'You', course: 'Instrument Rating', pct: '18%', last: '6 days' }
  ];

  readonly leadRows = [
    { initials: 'BB', name: 'Ben Barlow', instructor: 'Casey Aldridge', course: 'Private Pilot Airplane', pct: '72%', last: 'Today' },
    { initials: 'PC', name: 'Priya Chandra', instructor: 'Marisol Reyes', course: 'Instrument Rating', pct: '54%', last: 'Today' },
    { initials: 'OD', name: 'Omar Deveraux', instructor: 'Priya Anand', course: 'Commercial Pilot Airplane', pct: '38%', last: '2 days' },
    { initials: 'HK', name: 'Hana Kowalski', instructor: 'Theo Castellan', course: 'Private Pilot Airplane', pct: '61%', last: '3 days' },
    { initials: 'MO', name: 'Marcus Ostrander', instructor: 'Casey Aldridge', course: 'Commercial Multi-Engine', pct: '44%', last: '5 days' },
    { initials: 'LL', name: 'Linda Lundquist', instructor: 'Marisol Reyes', course: 'Private Pilot Airplane', pct: '29%', last: '8 days' }
  ];

  schoolFields = [
    { label: 'Flight School Name', value: this.schoolName },
    { label: 'Location', value: 'Scottsdale, AZ' },
    { label: 'Number Of Aircraft', value: '99' },
    { label: 'Remove Inactive Students', value: 'No' }
  ];

  readonly domainFields = [
    { label: 'Setup type', value: 'Full domain', note: '' },
    { label: 'Domain', value: 'learn.example.com', note: '' },
    { label: 'Auto join', value: 'Enabled', note: 'Students who register are added without approval.' },
    { label: 'Allow student/instructor selection', value: 'Disabled', note: '' }
  ];

  readonly basicFeatures = ['Courses', 'Quizzes', 'Course Endorsements'];
  readonly proFeatures = ['White Labeling', 'Course Progress', 'Instructors and Assignments', 'Profit Sharing'];

  readonly profitStats = [
    { label: 'Rate', value: '•••', accent: true },
    { label: '2026 total', value: '••••', accent: false },
    { label: 'Aug total', value: '••••', accent: false },
    { label: 'Aug reduction', value: '••••', accent: false },
    { label: 'Aug payout', value: '••••', accent: false }
  ];

  readonly adminFilters = [
    { label: 'Courses', value: 'All Courses' },
    { label: 'Students', value: 'All Students' },
    { label: 'Flight Instructor', value: 'All Instructors' },
    { label: 'Group', value: 'All Groups' }
  ];

  readonly enrollRows = [
    { course: 'Private Pilot Airplane', enrolled: '78', active: '61', pct: '64%', completed: '12' },
    { course: 'Instrument Rating', enrolled: '44', active: '33', pct: '51%', completed: '7' },
    { course: 'Commercial Pilot Airplane', enrolled: '29', active: '22', pct: '47%', completed: '4' },
    { course: 'Commercial Multi-Engine Course', enrolled: '18', active: '11', pct: '38%', completed: '2' }
  ];

  readonly adminMetrics = [
    { icon: '◍', kind: 'info', value: '169', label: 'Enrolled Students' },
    { icon: '✓', kind: 'success', value: '6h 12m', label: 'Avg Course Time Per Student' },
    { icon: '▤', kind: 'warning', value: '62', label: 'Current Assignments' },
    { icon: '!', kind: 'danger', value: '14', label: 'Assignments Past Due' },
    { icon: '★', kind: 'purple', value: '84%', label: 'ACS Test Average' },
    { icon: '◔', kind: 'muted', value: '9 Students', label: 'Awaiting ACS Test Upload' }
  ];

  readonly leaders = [
    { initials: 'CA', name: 'Casey Aldridge', students: '14', progress: '71%', acs: '88%', overdue: '1' },
    { initials: 'TC', name: 'Theo Castellan', students: '12', progress: '66%', acs: '85%', overdue: '3' },
    { initials: 'PA', name: 'Priya Anand', students: '9', progress: '58%', acs: '81%', overdue: '4' },
    { initials: 'MR', name: 'Marisol Reyes', students: '7', progress: '54%', acs: '79%', overdue: '6' }
  ];

  readonly difficultyModules = [
    { name: 'Weather & weather services', score: '68%', retakes: '31', time: '1h 12m' },
    { name: 'Performance & limitations', score: '72%', retakes: '24', time: '58m' },
    { name: 'Navigation & flight planning', score: '75%', retakes: '19', time: '1h 04m' },
    { name: 'Regulations & airspace', score: '81%', retakes: '12', time: '47m' },
    { name: 'Aerodynamics', score: '86%', retakes: '8', time: '39m' }
  ];

  readonly atRisk = [
    { initials: 'JL', name: 'Jamie Lindqvist', reason: '12% after 6 weeks', flag: 'Stalled' },
    { initials: 'SP', name: 'Sam Patel', reason: '2 assignments overdue', flag: 'Past due' },
    { initials: 'TB', name: 'Tessa Brandt', reason: 'ACS test not uploaded', flag: 'Missing' }
  ];

  readonly inactive = [
    { name: 'Jamie Lindqvist', days: '20d' },
    { name: 'Tessa Brandt', days: '27d' },
    { name: 'Devin Okafor', days: '9d' }
  ];

  constructor(private elementRef: ElementRef, private renderer: Renderer2) {
    this.setStep(0);
  }

  ngOnInit(): void {
    const themedImageUrl = document.documentElement.style.getPropertyValue('--dw-logo-url').trim();

    this.brandImageStyle = {
      'background-image': themedImageUrl
        ? 'var(--dw-auth-side-image-url, var(--dw-logo-url))'
        : 'var(--dw-default-auth-side-image-url, url(\'/assets/aviator-online/img/auth-side-default.webp\'))'
    };

    const role = this.modalConfig?.data?.role || 'student';
    this.schoolName = this.modalConfig?.data?.schoolName?.trim() || 'your school';
    this.steps = (STEPS_BY_ROLE[role] || STUDENT_STEPS).map(step => ({
      ...step,
      title: this.replaceSchoolName(step.title),
      description: this.replaceSchoolName(step.description)
    }));
    this.schoolFields = this.schoolFields.map(field => field.label === 'Flight School Name'
      ? { ...field, value: this.schoolName }
      : field);
    this.setStep(0);
  }

  ngAfterViewInit(): void {
    // The framework hosts this modal inside a PrimeNG p-dialog. Tag the real
    // dialog box so the scoped overrides in styles/features/
    // student-onboarding-modal.scss can size it and hide the framework header.
    const dialog = (this.elementRef.nativeElement as HTMLElement).closest('.p-dialog');
    if (dialog) {
      this.renderer.addClass(dialog, 'sob-dialog');
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') {
      this.setStep(this.stepIndex + 1);
    }
    if (event.key === 'ArrowLeft') {
      this.setStep(this.stepIndex - 1);
    }
  }

  goTo(index: number): void {
    this.setStep(index);
  }

  back(): void {
    this.setStep(this.stepIndex - 1);
  }

  next(): void {
    if (this.stepIndex === this.steps.length - 1) {
      this.finish();
      return;
    }
    this.setStep(this.stepIndex + 1);
  }

  trackByDotIndex(index: number): number {
    return index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private replaceSchoolName(value: string): string {
    return value.replace(/\{schoolName\}/g, this.schoolName);
  }

  private setStep(index: number): void {
    const last = this.steps.length - 1;
    this.stepIndex = Math.max(0, Math.min(last, index));
    this.currentStep = this.steps[this.stepIndex];
    this.backVisibility = this.stepIndex > 0 ? 'visible' : 'hidden';
    this.nextLabel = this.stepIndex === last ? (this.currentStep.ctaLabel || 'Done') : 'Next';
    this.dots = this.steps.map((step, i) => ({ index: i, isActive: i === this.stepIndex }));
  }

  private finish(): void {
    const result = new DwModalResult();
    result.cancelled = false;
    result.closeModal = true;
    this.closeModal.emit(result);
  }

  buttonClick(buttonConfig: DwButtonConfig): Observable<DwModalResult> {
    const result = new DwModalResult();
    result.cancelled = false;
    result.closeModal = true;
    return of(result);
  }
}
